/**
 * Centralized chunk-load error detection and single-retry reload logic.
 * Used by lazyWithRetry, route error boundary, and global handlers.
 */

const CHUNK_LOAD_MESSAGE_REGEX = /Loading chunk [\d]+ failed/i;

export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const err = error as { name?: string; message?: string };

  if (err.name === "ChunkLoadError") return true;
  if (typeof err.message === "string") return CHUNK_LOAD_MESSAGE_REGEX.test(err.message);

  return false;
}

export function getChunkRetryKey(scope: string): string {
  return `chunk-retry:${scope}`;
}

/**
 * Returns true if we should perform a reload (first attempt for this scope).
 * Call markChunkReloadAttempt(scope) before reloading.
 */
export function shouldReloadForChunkError(scope: string): boolean {
  return !sessionStorage.getItem(getChunkRetryKey(scope));
}

export function markChunkReloadAttempt(scope: string): void {
  sessionStorage.setItem(getChunkRetryKey(scope), "true");
}

export function clearChunkReloadAttempt(scope: string): void {
  sessionStorage.removeItem(getChunkRetryKey(scope));
}

function tryReloadForChunkError(scope: string): boolean {
  if (!shouldReloadForChunkError(scope)) {
    clearChunkReloadAttempt(scope);
    return false;
  }
  markChunkReloadAttempt(scope);
  window.location.reload();
  return true;
}

function getMessageFromError(event: ErrorEvent | PromiseRejectionEvent): string {
  if (event instanceof ErrorEvent) return event.message ?? "";
  if ("reason" in event && event.reason != null) {
    const r = event.reason;

    if (typeof r === "string") return r;
    if (typeof r === "object" && r !== null && "message" in r && typeof (r as Error).message === "string") {
      return (r as Error).message;
    }
  }
  return "";
}

/**
 * Registers global handlers for chunk load errors (safety net when error
 * doesn't surface through lazy() or router). Only one reload is attempted per scope.
 */
export function registerGlobalChunkErrorHandler(scope = "global"): void {
  const handleChunkError = (message: string): boolean => {
    if (!message || !CHUNK_LOAD_MESSAGE_REGEX.test(message)) return false;
    return tryReloadForChunkError(scope);
  };

  window.addEventListener("error", (event: ErrorEvent) => {
    if (handleChunkError(event.message)) {
      event.preventDefault();
      event.stopPropagation();
    }
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const message = getMessageFromError(event);

    if (handleChunkError(message)) {
      event.preventDefault();
    }
  });
}
